import { useBookingsEnabled } from 'features/Booking'
import { useSupplierBookingRequestEnabled } from 'features/BookingRequests'
import { useIntegrationsEnabled } from 'features/Integrations'
import { History } from 'history'
import { inject, observer } from 'mobx-react'
import { useObserver } from 'mobx-react-lite'
import { Component, createElement, FC, useEffect } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { AuthStore } from 'service/Auth'
import { authorize, hasRoles, Role, UserRoles } from 'service/roles'
import { UIStore } from 'service/UIStore'
import { useAuth } from 'service/useAuth'

import { asyncComponent } from './Bundle'

const QuoteResults = asyncComponent(
  () => import(/* webpackChunkName: "QuoteResults" */ 'features/BookingFlow/QuoteResults/QuoteResultsRoot')
)
const QuoteRequest = asyncComponent(
  () => import(/* webpackChunkName: "QuoteRequest" */ 'features/BookingFlow/QuoteRequest/QuoteRequestRoot')
)
const Booked = asyncComponent(() => import(/* webpackChunkName: "Booked" */ 'features/ShipmentDetails/view/Booked'))
const Support = asyncComponent(() => import(/* webpackChunkName: "Support" */ 'features/Support/Support'))
const Settings = asyncComponent(() => import(/* webpackChunkName: "Settings" */ 'features/Settings/SettingsRoot'))
const ManualQuotes = asyncComponent(
  () => import(/* webpackChunkName: "ManualQuotes" */ 'features/ManualQuotes/ManualQuotesList/ManualQuotesListRoot')
)
const Shipment = asyncComponent(
  () => import(/* webpackChunkName: "Shipment" */ 'features/ShipmentDetails/route/ShipmentRoot')
)
const CompanyUserList = asyncComponent(
  () => import(/* webpackChunkName: "CompanyUserList" */ 'features/Settings/CompanyUsers/CompanyUserListRoot')
)
const CompanyUserDetail = asyncComponent(
  () => import(/* webpackChunkName: "CompanyUserDetail" */ 'features/Settings/CompanyUsers/CompanyUserDetailRoot')
)
const CompanyDocuments = asyncComponent(
  () => import(/* webpackChunkName: "CompanyDocuments" */ 'features/Settings/CompanyDocuments/CompanyDocumentsRoot')
)
const CompanyActivity = asyncComponent(
  () => import(/* webpackChunkName: "CompanyActivity" */ 'features/Settings/CompanyActivity/CompanyActivityRoot')
)
const Shipments = asyncComponent(
  () => import(/* webpackChunkName: "Shipments" */ 'features/ShipmentsList/route/ShipmentsRoot')
)
const Tracking = asyncComponent(() => import(/* webpackChunkName: "Tracking" */ 'features/TrackTrace/TrackingRoot'))

const Reports = asyncComponent(() => import(/* webpackChunkName: "Reports" */ 'features/Reports/route/ReportsRoot'))
const ReportTemplate = asyncComponent(
  () => import(/* webpackChunkName: "Report" */ 'features/Reports/route/ReportTemplateRoot')
)
const Billing = asyncComponent(() => import(/* webpackChunkName: "Billing" */ 'features/Billing/route/BillingRoot'))

const NotificationCenter = asyncComponent(
  () => import(/* webpackChunkName: "NotificationCenter" */ 'features/NotificationCenter/NotificationCenterRoot')
)
const NotificationsSettings = asyncComponent(
  () =>
    import(/* webpackChunkName: "NotificationsSettings" */ 'features/NotificationsSettings/NotificationsSettingsRoot')
)
const NetworkRoot = asyncComponent(() => import(/* webpackChunkName: "Network" */ 'features/Directory/NetworkRoot'))
const BookingTemplatesList = asyncComponent(
  () =>
    import(
      /* webpackChunkName: "BookingTemplatesList" */ 'features/BookingTemplates/BookingTemplatesList/BookingTemplatesListRoot'
    )
)

const BookingTemplateDetail = asyncComponent(
  () =>
    import(
      /* webpackChunkName: "BookingTemplateDetail" */ 'features/BookingTemplates/BookingTemplateDetail/BookingTemplateDetailRoot'
    )
)
const BookingRequestsList = asyncComponent(
  () =>
    import(
      /* webpackChunkName: "BookingRequestsList" */ 'features/BookingRequests/BookingRequestsList/BookingRequestsListRoot'
    )
)
const BookingRequestDetail = asyncComponent(
  () =>
    import(
      /* webpackChunkName: "BookingRequestDetail" */ 'features/BookingRequests/BookingRequestDetail/BookingRequestDetailRoot'
    )
)

const BookingsList = asyncComponent(
  () => import(/* webpackChunkName: "BookingsList " */ 'features/Booking/features/list/routes')
)
const BookingBooked = asyncComponent(
  () => import(/* webpackChunkName: "BookingBooked" */ 'features/Booking/features/booked/routes')
)
const BookingDetails = asyncComponent(
  () => import(/* webpackChunkName: "BookingDetails " */ 'features/Booking/features/details/routes')
)

const PurchaseOrdersMicroFrontEnd = asyncComponent(
  () => import(/* webpackChunkName: "POMicroFrontEnd" */ 'features/PurchaseOrders/pages/POMicroFrontEnd')
)

const Integrations = asyncComponent(() => import('features/Integrations/IntegrationsRoot'))

const SubscriptionDetails = asyncComponent(
  () =>
    import(/* webpackChunkName: "SubscriptionDetails" */ 'features/Integrations/subscriptions/SubscriptionDetailRoot')
)
const ApiKeyDetails = asyncComponent(
  () => import(/* webpackChunkName: "ApiKeyDetails" */ 'features/Integrations/tokens/ApiTokenDetailRoot')
)

// Login routes
const Login = asyncComponent(() => import(/* webpackChunkName: "Login" */ 'features/Login/LoginRoot'))
const LoginAs = asyncComponent(() => import(/* webpackChunkName: "LoginAs" */ 'features/Login/LoginAsRoot'))
const ResetPassword = asyncComponent(
  () => import(/* webpackChunkName: "ResetPassword" */ 'features/Login/ResetPassword/ResetPasswordRoot')
)
const Activate = asyncComponent(() => import(/* webpackChunkName: "Activate" */ 'features/Login/Activate/ActivateRoot'))
const Activation = asyncComponent(
  () => import(/* webpackChunkName: "Activation" */ 'features/Login/Activate/ActivationRoot')
)

type RouteProps = {
  path: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  component: any
  exact?: boolean
  strict?: boolean
  authorizedRoles?: Role[]
  redirectPath?: string
}

const SecuredRoute: FC<RouteProps> = (props) => {
  return useObserver(() => {
    const { authorizedRoles = UserRoles, redirectPath } = props
    const {
      state: { authenticated, customer, user },
      requireAuth,
    } = useAuth()

    useEffect(() => {
      requireAuth()
    }, [authenticated])

    if (!customer || !user) {
      return null
    }

    return hasRoles(user, authorizedRoles) ? (
      createElement(Route, props)
    ) : (
      <Redirect to={{ pathname: redirectPath || '/' }} />
    )
  })
}

@inject('uiStore', 'authStore', 'history')
@observer
class UnsecuredRoute extends Component<
  RouteProps & {
    authStore?: AuthStore
    uiStore?: UIStore
    history?: History
    redirect?: boolean
  },
  {}
> {
  needsRedirect() {
    // route needs redirect if there is a token already
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    return this.props.redirect && this.props.authStore.state.token
  }

  UNSAFE_componentWillMount() {
    if (this.needsRedirect()) {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      this.props.authStore.requireAuth()
    }
  }

  render() {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    if (this.needsRedirect() && this.props.authStore.state.customer) {
      // redirect only when customer is really loaded
      // otherwise with invalid token there will be a loop of redirections login-shipments
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      this.props.history.replace('/')
    }
    if (this.needsRedirect()) {
      return null
    }
    return createElement(Route, this.props)
  }
}

export const Routes: FC = () => {
  const isBookingsEnabled = useBookingsEnabled()
  const isIntegrationsEnabled = useIntegrationsEnabled()
  const isSupplierBookingRequestEnabled = useSupplierBookingRequestEnabled()

  return (
    <Switch>
      <UnsecuredRoute path="/loginas" component={LoginAs} />
      <UnsecuredRoute path="/login" component={Login} redirect />
      <UnsecuredRoute path="/activate" component={Activate} />
      <UnsecuredRoute path="/activation" component={Activation} />
      <UnsecuredRoute path="/reset-password" component={ResetPassword} redirect />

      {/* Redirects should go before component that can match */}
      <Redirect from="/settings/documents" to="/company/documents" />

      <SecuredRoute path="/shipments" component={Shipments} />
      <SecuredRoute path="/shipment/:id/booked" component={Booked} />
      <SecuredRoute path="/shipment/:id" component={Shipment} />
      <SecuredRoute path="/tracking" component={Tracking} authorizedRoles={authorize.membersOrBilling} />
      <SecuredRoute path="/support" component={Support} />
      <SecuredRoute path="/settings" component={Settings} />
      <SecuredRoute exact path="/company/users" component={CompanyUserList} authorizedRoles={authorize.managers} />
      <SecuredRoute path="/company/users/:id" component={CompanyUserDetail} authorizedRoles={authorize.admins} />
      <SecuredRoute path="/company/documents" component={CompanyDocuments} authorizedRoles={authorize.managers} />
      <SecuredRoute path="/company/activity" component={CompanyActivity} authorizedRoles={authorize.managers} />

      <SecuredRoute path="/quotes" component={ManualQuotes} authorizedRoles={authorize.membersOrBilling} />
      <SecuredRoute path="/quote-request" component={QuoteRequest} authorizedRoles={authorize.membersOrBilling} />

      <SecuredRoute path="/quote-sheet" component={QuoteResults} />
      <SecuredRoute path="/reports/edit/:id?" component={ReportTemplate} authorizedRoles={authorize.membersOrBilling} />
      <SecuredRoute path="/reports" component={Reports} authorizedRoles={authorize.membersOrBilling} />
      <SecuredRoute path="/invoices" component={Billing} authorizedRoles={authorize.membersOrBilling} />
      <SecuredRoute path="/notification-center/settings" component={NotificationsSettings} />
      <SecuredRoute path="/notification-center" component={NotificationCenter} />
      <SecuredRoute path="/directory" component={NetworkRoot} authorizedRoles={authorize.managers} />

      <SecuredRoute exact path="/booking-templates" component={BookingTemplatesList} />
      <SecuredRoute
        path="/booking-templates/:id"
        component={BookingTemplateDetail}
        authorizedRoles={authorize.managers}
      />
      {isSupplierBookingRequestEnabled && (
        <SecuredRoute exact path="/booking-requests" component={BookingRequestsList} />
      )}
      {isSupplierBookingRequestEnabled && (
        <SecuredRoute path="/booking-requests/:id" component={BookingRequestDetail} />
      )}

      {isBookingsEnabled && <SecuredRoute path="/bookings" exact component={BookingsList} />}
      {isBookingsEnabled && <SecuredRoute path="/bookings/:id/booked" component={BookingBooked} />}
      {isBookingsEnabled && <SecuredRoute path="/bookings/:id" component={BookingDetails} />}

      <SecuredRoute path="/purchase-orders" component={PurchaseOrdersMicroFrontEnd} authorizedRoles={authorize.all} />
      <SecuredRoute
        path="/connect-supplier"
        component={PurchaseOrdersMicroFrontEnd}
        authorizedRoles={authorize.managers}
      />
      <Route
        path="/book/:id/:idx"
        render={(props) => <Redirect to={`/quote-sheet/${props.match.params.id}/book/${props.match.params.idx}`} />}
      />

      {isIntegrationsEnabled && <SecuredRoute path="/integrations" exact component={Integrations} />}
      {isIntegrationsEnabled && <SecuredRoute path="/integrations/subscriptions/:id" component={SubscriptionDetails} />}
      {isIntegrationsEnabled && <SecuredRoute path="/integrations/api-tokens/:tokenId" component={ApiKeyDetails} />}

      <Redirect exact path="/" to="/shipments" />
      <Redirect to={{ pathname: '/' }} />
    </Switch>
  )
}
